import * as Sentry from "@sentry/react";
import cx from "classnames";
import { Form, Formik } from "formik";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Checkbox from "../../common/components/Forms/Checkbox/Checkbox";
import { addAddress, getAddress, setCurrentPageProgress } from "../api/ApiAppForm";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import AppFormField from "../components/AppFormField";
import { AddressType } from "../enums/AddressType";
import { PageType } from "../enums/PageType";
import {
    IAddressInfo,
    IAnswerProps,
    IApplicantInfo,
    IPageInfo
} from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";
import { useAFForm } from "../hooks/useAFForm";
import styles from "../scss/AFAddressPage.module.scss";
import { redirectTo404Page } from "../shared/AppFormHelper";

interface IAFAddressPage {
    applicationId: string;
    applicant: string;
}

const AddressQuestionList = [
    {
        id: 1,
        questionLabel: "",
        questionText: "What is [firstName] home address?",
        controlTypeId: 16,
        controlType: "AddressField",
        placeholder: "Start typing your home address",
        questionType: "QuestionList"
    },
    {
        id: 2,
        questionLabel: "",
        questionText: "What is [firstName] postal address?",
        controlTypeId: 16,
        controlType: "AddressField",
        placeholder: "Start typing your postal address",
        questionType: "QuestionList"
    }
];

const AFAddressPage: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFAddressPage>();
    const applicationId = params.applicationId;
    const applicantParams = params.applicant;
    const { setError } = useContext<IViewContext>(ViewContext);

    const [isQuestionReady, setIsQuestionReady] = useState(false);
    const [isSameAddress, setIsSameAddress] = useState(true);

    const [homeAddress, setHomeAddress] = useState<IAnswerProps>();
    const [postalAddress, setPostalAddress] = useState<IAnswerProps>();
    const [questionReady, setQuestionReady] = useState(false);
    const [firstName, setFirstName] = useState("");
    const formRef = useRef(null);

    const { selectors, actions } = useContext(AppFormControllerContext);
    const applicant = selectors.getCurrentApplicant(applicantParams);
    const {
        getCurrentPage,
        filterAddressLabel,
        checkValidApplication
    } = useAFForm({ pageType: PageType.Address, currentClientNumber: applicant?.clientNumber ?? 1 });



    const handleBack = useCallback((e): void => {
        e.preventDefault();
        if (applicantParams === "applicant1") {
            history.push(`/app-form/submit/${applicationId}/payment`);
        } else {
            const applicantList = selectors.getApplicantList();
            const currentApplicant = applicantList.find((x) => x.params == applicantParams);
            const previousApplicant = applicantList.find((x) => x.clientNumber === currentApplicant.clientNumber - 1);
            history.push(`/app-form/submit/${applicationId}/applicant${previousApplicant.clientNumber}/address`);
        }
    }, []);

    const handleNextPage = (): void => {
        setIsQuestionReady(false);
        if (formRef.current) {
            formRef.current?.reset();
            setIsSameAddress(true);
        }
        const applicantList = selectors.getApplicantList();
        const currentApplicant = applicantList.find((x) => x.params == applicantParams);
        const nextApplicant = applicantList.find((x) => x.clientNumber === currentApplicant.clientNumber + 1 && !x.isChild);
        if (!nextApplicant) {
            const pageInfo: IPageInfo = {
                pageType: PageType.Consent,
                currentClientNumber: 1
            }
            setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
            )
            history.push(`/app-form/submit/${applicationId}/consent/disclosure`);
            return;
        }
        const pageInfo: IPageInfo = {
            pageType: PageType.Address,
            currentClientNumber: nextApplicant.clientNumber
        }
        setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
        )
        history.push(
            `/app-form/submit/${applicationId}/applicant${nextApplicant?.clientNumber}/address`
        );
    };

    const getApplicant = (applicantFirstName): IApplicantInfo => {
        const applicantList = selectors.getApplicantList();
        return applicantList.find((i) => i.firstName === applicantFirstName);
    };

    const getQuestion = (addresses: Array<IAddressInfo>): void => {
        const questions = AddressQuestionList;
        if (!questions?.length) return;
        const info = selectors.getCurrentApplicant(applicantParams);
        setFirstName(info?.firstName);
        addresses.forEach((_i) => {
            if (parseInt(_i.addressType) === AddressType.HOME_ADDRESS) {
                setIsSameAddress(_i.isSameWithPostalAddress);
                setHomeAddress({
                    questionId: parseInt(_i.addressType),
                    answer: JSON.stringify(_i)
                });
            } else if (parseInt(_i.addressType) === AddressType.POSTAL_ADDRESS) {
                setPostalAddress({
                    questionId: parseInt(_i.addressType),
                    answer: JSON.stringify(_i)
                });
            }
        });
        setIsQuestionReady(true);
    };

    const handleAnswers = (answerParam: IAnswerProps): void => {
        switch (answerParam?.questionId) {
            case AddressType.HOME_ADDRESS:
                setHomeAddress(answerParam);
                break;
            case AddressType.POSTAL_ADDRESS:
                setPostalAddress(answerParam);
                break;
            default:
        }
    };

    const handleSaveAddress = async (): Promise<void> => {
        const answerJson = homeAddress?.answer;
        const answerObj = answerJson? JSON.parse(answerJson) : null;
        const homeAddressInfo = {
            applicantId: getApplicant(firstName)?.applicantId,
            policyOwnerId: getApplicant(firstName)?.appPolicyOwnerId,
            isApplicant: true,
            addressType: "Home",
            isSameWithPostalAddress: isSameAddress,
            address: answerObj
        };
        const promises = [
            addAddress(
                selectors.getApplicationId(),
                selectors.getQuoteGroupId(),
                homeAddressInfo
            )
        ];
        if (!isSameAddress) {
            const answerObj = JSON.parse(postalAddress?.answer);
            const postalAddressInfo = {
                applicantId: getApplicant(firstName)?.applicantId,
                policyOwnerId: getApplicant(firstName)?.appPolicyOwnerId,
                isApplicant: true,
                addressType: "Postal",
                isSameWithPostalAddress: isSameAddress,
                address: answerObj
            };
            promises.push(
                addAddress(
                    selectors.getApplicationId(),
                    selectors.getQuoteGroupId(),
                    postalAddressInfo
                )
            );
        }
        await Promise.all(promises)
            .then()
            .catch(() => {
                setError("There was a problem saving address details.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for Applicant ${applicantParams} on ApplicationId ${applicationId} on the Address page. There was a problem saving address details.`, "error");
                }
            });
    };

    const handleSubmit = (): void => {
        handleSaveAddress().finally(() => handleNextPage());
    };

    const handleFilterLabel = (label: string): string => {
        const isMoreApplicant = selectors.getApplicantList().length > 1;
        const info = selectors.getCurrentApplicant(applicantParams);
        return filterAddressLabel(label, info, isMoreApplicant);
    };

    const handleGetAddress = (): void => {
        const applicationId = selectors.getApplicationId();
        const info = selectors.getCurrentApplicant(applicantParams);
        const applicantId = getApplicant(info?.firstName)?.applicantId;

        setIsQuestionReady(false);
        getAddress(applicationId, selectors.getQuoteGroupId(), applicantId)
            .then((res) => {
                getQuestion(res);
            })
            .catch(() => {
                getQuestion([]);
            });
    };


    const getCurrentQuestion = (type: number) => {
        const address = type === AddressType.HOME_ADDRESS ? homeAddress : postalAddress;
        const answer = address?.questionId ? address?.answer : "";
        if (answer)
            return {
                ...AddressQuestionList.find((_i) => _i.id === type),
                answer
            };
        return AddressQuestionList.find((_i) => _i.id === type);
    };

    const handleInvalidForm = (): boolean => {
        return !homeAddress?.answer || (!isSameAddress && !postalAddress?.answer);
    };

    const keyDownHandler = useCallback((event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            handleBack(event);
        }
    }, []);


    useEffect(() => {
        if (!applicantParams) return;
        if (!applicant || !applicant?.personId) redirectTo404Page();

        checkValidApplication(applicationId, selectors);
        setIsSameAddress(true);
        handleGetAddress();
    }, [applicant?.clientNumber]);


    useEffect(() => {
        document.addEventListener("keydown", keyDownHandler);
        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, []);

    return (
        <AFLayout currentPage={getCurrentPage()}>
            <div className={cx(styles["af-address-page"], styles["layout"])}>
                <div className={styles["app-form-container"]}>
                    <div
                        className={cx(
                            styles["appform-question-form"]
                        )}>
                        {isQuestionReady && (
                            <Formik
                                innerRef={formRef}
                                initialValues={{}}
                                onSubmit={handleSubmit}>
                                {() => (
                                    <Form 
                                        className={cx(
                                            styles["fa-question-form"],
                                            styles["address-page"]
                                        )}>
                                        <div
                                            className={cx(
                                                styles["question-form"],
                                                "layout-flex-column--centered"
                                            )}>
                                            <label className={cx(styles["question-form-label"])}>
                                                {handleFilterLabel("What is [firstName] home address?")}
                                            </label>

                                            <div
                                                className={styles["question-form-field"]}>
                                                <AppFormField
                                                    question={getCurrentQuestion(
                                                        AddressType.HOME_ADDRESS
                                                    )}
                                                    answerChanges={handleAnswers}
                                                    fieldReady={setQuestionReady}
                                                />
                                                <Checkbox
                                                    id="is-same-address"
                                                    name="is-same-address"
                                                    checked={isSameAddress}
                                                    onChange={(e) => {
                                                        setIsSameAddress(
                                                            e.target.checked
                                                        );
                                                    }}
                                                    onBlur={() => {}}
                                                    size="m"
                                                    className={cx(
                                                        "gap-ml",
                                                        styles["af-terms-check"]
                                                    )}
                                                    label={
                                                        <span
                                                            className={cx(
                                                                "space-pre-m",
                                                                styles[
                                                                    "af-terms-check--label"
                                                                ]
                                                            )}>
                                                            This is also my postal address
                                                        </span>
                                                    }
                                                />
                                            </div>
                                        </div>
                                        {!isSameAddress && (
                                            <div
                                                className={cx(
                                                    styles["question-form"],
                                                    "layout-flex-column--centered"
                                                )}>
                                                <label className={cx(styles["question-form-label"])}>
                                                    {handleFilterLabel("What is [firstName] postal address?")}
                                                </label>

                                                <div
                                                    className={
                                                        styles["question-form-field"]
                                                    }>
                                                    <AppFormField
                                                        question={getCurrentQuestion(
                                                            AddressType.POSTAL_ADDRESS
                                                        )}
                                                        answerChanges={handleAnswers}
                                                        fieldReady={setQuestionReady}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <AFNavigation
                                            handleBack={handleBack}
                                            skipIsVisible={false}
                                            forwardIsVisible={true}
                                            forwardIsDisabled={handleInvalidForm()}
                                            forwardType="submit"
                                            forwardText={"Next"}
                                        />
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </div>
                </div>
            </div>
        </AFLayout>
    );
};

export default AFAddressPage;
